import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, Cookie, Settings } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface CookiePreferences {
  accepted: boolean;
  essential: boolean;
  analytics: boolean;
  preferences: boolean;
  timestamp: number;
}

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    accepted: false,
    essential: true, // Always true, cannot be disabled
    analytics: false,
    preferences: true,
    timestamp: Date.now(),
  });

  useEffect(() => {
    // Check if user has already given consent
    const stored = localStorage.getItem('cookie_consent');
    
    if (stored) {
      try {
        const parsed: CookiePreferences = JSON.parse(stored);
        
        // Re-show consent banner every 6 months (182 days)
        const sixMonthsAgo = Date.now() - (182 * 24 * 60 * 60 * 1000);
        if (parsed.timestamp < sixMonthsAgo) {
          setShowBanner(true);
        } else {
          setPreferences(parsed);
        }
      } catch (e) {
        console.error('Error parsing cookie consent:', e);
        setShowBanner(true);
      }
    } else {
      // First visit - show banner after 1 second
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    const toSave = { ...prefs, timestamp: Date.now() };
    localStorage.setItem('cookie_consent', JSON.stringify(toSave));
    setPreferences(toSave);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    savePreferences({
      accepted: true,
      essential: true,
      analytics: true,
      preferences: true,
      timestamp: Date.now(),
    });
  };

  const acceptEssentialOnly = () => {
    savePreferences({
      accepted: true,
      essential: true,
      analytics: false,
      preferences: false,
      timestamp: Date.now(),
    });
  };

  const saveCustomPreferences = () => {
    savePreferences({ ...preferences, accepted: true });
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
        <Card className="max-w-4xl mx-auto p-6 shadow-2xl border-2 animate-in slide-in-from-bottom duration-500">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <Cookie className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">We use cookies</h3>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                This website uses cookies to improve your experience and analyze site traffic. 
                By continuing, you agree to our use of cookies. Essential cookies are required 
                for the site to function properly.
              </p>

              <div className="flex flex-wrap gap-2">
                <Button onClick={acceptAll} className="flex-1 sm:flex-none">
                  Accept All
                </Button>
                <Button 
                  onClick={acceptEssentialOnly} 
                  variant="outline"
                  className="flex-1 sm:flex-none"
                >
                  Essential Only
                </Button>
                <Button 
                  onClick={() => setShowPreferences(true)} 
                  variant="ghost"
                  className="flex-1 sm:flex-none"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Manage Preferences
                </Button>
                <Link to="/privacy-policy#cookies-section">
                  <Button variant="link" className="text-xs">
                    Read our Cookie Policy
                  </Button>
                </Link>
              </div>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={acceptEssentialOnly}
              className="flex-shrink-0"
              aria-label="Dismiss cookie banner"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>

      {/* Preferences Modal */}
      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cookie Preferences</DialogTitle>
            <DialogDescription>
              Manage your cookie preferences. Essential cookies are always enabled as they are 
              necessary for the website to function properly.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Essential Cookies */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label className="text-base font-semibold">Essential Cookies</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Required for the website to function. Cannot be disabled.
                  </p>
                </div>
                <Switch checked={true} disabled />
              </div>
              <div className="text-xs text-muted-foreground ml-1">
                Used for: Authentication, session management, security, CSRF protection
              </div>
            </div>

            {/* Preference Cookies */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label htmlFor="preferences-cookies" className="text-base font-semibold">
                    Preference Cookies
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Remember your settings and preferences for a better experience.
                  </p>
                </div>
                <Switch
                  id="preferences-cookies"
                  checked={preferences.preferences}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, preferences: checked })
                  }
                />
              </div>
              <div className="text-xs text-muted-foreground ml-1">
                Used for: Theme (light/dark mode), language, notification settings, chart preferences
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Label htmlFor="analytics-cookies" className="text-base font-semibold">
                    Analytics Cookies
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Help us understand how visitors interact with the website (anonymous data).
                  </p>
                </div>
                <Switch
                  id="analytics-cookies"
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, analytics: checked })
                  }
                />
              </div>
              <div className="text-xs text-muted-foreground ml-1">
                Used for: Page views, session duration, feature usage, performance monitoring (anonymized)
              </div>
            </div>
          </div>

          <div className="flex justify-between gap-2 pt-4 border-t">
            <Link to="/privacy-policy#cookies-section">
              <Button variant="link" className="text-sm">
                Cookie Policy
              </Button>
            </Link>
            <div className="flex gap-2">
              <Button variant="outline" onClick={acceptEssentialOnly}>
                Essential Only
              </Button>
              <Button onClick={saveCustomPreferences}>
                Save Preferences
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
